import { type Qualification } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { useLicenseFieldsRequirementsFlag } from "@src/appV2/Licenses/useLicenseFieldsRequirementsFlag";
import { useLicenseNumberRequirementsFlag } from "@src/appV2/Licenses/useLicenseNumberRequirementsFlag";
import { SpecialQualifications } from "@src/appV2/Qualifications/types";
import { useMemo } from "react";

interface UseAddLicenseFormFieldBehaviorProps {
  currentQualification?: Qualification;
  selectedState?: string;
}

export interface AddLicenseFormFieldBehavior {
  state: {
    hidden: boolean;
  };
  licenseNumber: {
    hidden: boolean;
    required: boolean;
  };
}

export function useAddLicenseFormFieldBehavior(
  props: UseAddLicenseFormFieldBehaviorProps
): AddLicenseFormFieldBehavior | undefined {
  const { currentQualification, selectedState } = props;
  const licenseFieldsRequirements = useLicenseFieldsRequirementsFlag();
  const licenseNumberRequirements = useLicenseNumberRequirementsFlag();

  return useMemo(() => {
    const qualificationName = currentQualification?.attributes.name;

    if (!isDefined(qualificationName)) {
      return undefined;
    }

    return {
      state: {
        hidden: licenseFieldsRequirements[qualificationName]?.state === "hidden",
      },
      licenseNumber: {
        hidden:
          licenseFieldsRequirements[qualificationName]?.["license-number"] === "hidden" ||
          // Special case for CNA in Illinois, this was taken from old license code.
          (qualificationName === SpecialQualifications.CNA && selectedState === "Illinois"),
        required: isDefined(selectedState)
          ? (licenseNumberRequirements?.[selectedState]?.includes(qualificationName) ?? false)
          : false,
      },
    };
  }, [
    licenseFieldsRequirements,
    currentQualification?.attributes.name,
    selectedState,
    licenseNumberRequirements,
  ]);
}
